import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  createIntegrationApiKey,
  fetchIntegrationApiKeys,
  revokeIntegrationApiKey,
  rotateIntegrationApiKey,
} from '@/features/webinar/webinarThunks';
import {
  deleteWorkspaceIntegration,
  fetchWorkspaceIntegrations,
  setupWorkspaceIntegration,
} from '@/features/workspace/workspaceThunks';
import Title from '@/components/ui/Title';
import { InputLabel } from '@/components/ui/InputLabel';
import { Button } from '@/components/ui/button';
import type { IntegrationApiKeyCreatedDto, IntegrationApiKeyDto } from '@/features/webinar/types';
import { ChevronRight, KeyRound, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { Skeleton } from '@/components/ui/skeleton';
import { zHttpsUrlWithValidHostname } from '@/lib/validation/httpsIntegrationUrlSchema';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import StripeConnectRow from './StripeConnectRow';
import { isStripeConnectEnabled } from '@/lib/stripeConnect';

type FormValues = {
  name: string;
};

type ActiveCampaignFormValues = {
  apiUrl: string;
  apiKey: string;
};

type SheetIntegrationStep = 'choose' | 'api' | 'activeCampaign';

export default function IntegrationsPage() {
  const { t } = useTranslation('integrations');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const isWorkspaceAdmin = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.currentUserRole === 'ADMIN',
  );
  const { integrationApiKeys } = useAppSelector((state: RootState) => state.webinar);
  const { integrations } = useAppSelector((state: RootState) => state.workspace);
  const [createdApiKey, setCreatedApiKey] = useState<IntegrationApiKeyCreatedDto | null>(null);
  const [rotatedApiKey, setRotatedApiKey] = useState<IntegrationApiKeyCreatedDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetStep, setSheetStep] = useState<SheetIntegrationStep>('choose');
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  const [apiDetailsSheetOpen, setApiDetailsSheetOpen] = useState(false);
  const [submittingApiKey, setSubmittingApiKey] = useState(false);
  const [submittingActiveCampaign, setSubmittingActiveCampaign] = useState(false);
  const [deletingActiveCampaign, setDeletingActiveCampaign] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!workspaceId) return;
      setLoading(true);
      try {
        await Promise.all([
          dispatch(fetchIntegrationApiKeys({ workspaceId })),
          dispatch(fetchWorkspaceIntegrations(workspaceId)),
        ]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [dispatch, workspaceId]);

  useEffect(() => {
    if (!sheetOpen) {
      setCreatedApiKey(null);
      setError(null);
    }
  }, [sheetOpen]);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('validation.nameRequired'))
          .max(80, t('validation.nameMaxLength'))
          .refine((val) => val.trim().length > 0, {
            message: t('validation.nameRequired'),
          }),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
    mode: 'onChange',
  });

  const activeCampaignSchema = useMemo(
    () =>
      z.object({
        apiUrl: zHttpsUrlWithValidHostname({
          required: t('validation.urlRequired'),
          invalid: t('validation.urlInvalid'),
          mustBeHttps: t('validation.urlMustBeHttps'),
          invalidDomain: t('validation.urlInvalidDomain'),
        }),
        apiKey: z
          .string()
          .min(1, t('validation.apiKeyRequired'))
          .refine((val) => val.trim().length > 0, { message: t('validation.apiKeyRequired') }),
      }),
    [t],
  );

  const activeCampaignForm = useForm<ActiveCampaignFormValues>({
    resolver: zodResolver(activeCampaignSchema),
    defaultValues: { apiUrl: '', apiKey: '' },
    mode: 'onChange',
  });

  const activeCampaignIntegration = useMemo(
    () => integrations.find((i) => i.type === 'ACTIVE_CAMPAIGN') ?? null,
    [integrations],
  );

  const activeWebhooksApiKeysCount = useMemo(
    () =>
      (integrationApiKeys ?? []).filter(
        (apiKey: IntegrationApiKeyDto) =>
          apiKey.status !== 'REVOKED' && apiKey.status !== 'revoked',
      ).length,
    [integrationApiKeys],
  );

  const webhooksStatus = useMemo(
    () =>
      activeWebhooksApiKeysCount > 0
        ? { label: t('status.active'), variant: 'live' as const }
        : { label: t('status.inactive'), variant: 'draft' as const },
    [activeWebhooksApiKeysCount, t],
  );

  useEffect(() => {
    if (apiDetailsSheetOpen && activeWebhooksApiKeysCount === 0) {
      setApiDetailsSheetOpen(false);
    }
  }, [activeWebhooksApiKeysCount, apiDetailsSheetOpen]);

  const activeCampaignStatus = useMemo(() => {
    const enabled = activeCampaignIntegration?.enabled ?? false;
    const status = activeCampaignIntegration?.status ?? null;

    if (!enabled) return { label: t('status.inactive'), variant: 'draft' as const };
    if (status === 'CONNECTED') return { label: t('status.active'), variant: 'live' as const };
    if (status === 'ERROR') return { label: t('status.error'), variant: 'destructive' as const };
    if (status === 'DISCONNECTED')
      return { label: t('status.disconnected'), variant: 'draft' as const };
    return { label: t('status.unknown'), variant: 'draft' as const };
  }, [activeCampaignIntegration, t]);

  const isActiveCampaignActive = useMemo(
    () =>
      (activeCampaignIntegration?.enabled ?? false) &&
      activeCampaignIntegration?.status === 'CONNECTED',
    [activeCampaignIntegration],
  );

  const handleDeleteActiveCampaign = async () => {
    if (!workspaceId) {
      toast.error(t('toast.workspaceNotFound'));
      return;
    }

    setDeletingActiveCampaign(true);
    try {
      const result = await dispatch(
        deleteWorkspaceIntegration({ workspaceId, providerType: 'ACTIVE_CAMPAIGN' }),
      );

      if (deleteWorkspaceIntegration.fulfilled.match(result)) {
        toast.success(t('toast.activeCampaignDeleted'));
        dispatch(fetchWorkspaceIntegrations(workspaceId));
        setDetailsSheetOpen(false);
      } else if (deleteWorkspaceIntegration.rejected.match(result)) {
        toast.error(result.payload?.message ?? t('toast.activeCampaignDeleteError'));
      }
    } finally {
      setDeletingActiveCampaign(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!workspaceId) {
      toast.error(t('toast.workspaceNotFound'));
      return;
    }

    setError(null);
    setCreatedApiKey(null);
    setSubmittingApiKey(true);

    try {
      const result = await dispatch(
        createIntegrationApiKey({
          workspaceId,
          input: { name: data.name.trim() },
        }),
      );

      if (createIntegrationApiKey.fulfilled.match(result)) {
        setCreatedApiKey(result.payload);
        form.reset();
        if (workspaceId) {
          dispatch(fetchIntegrationApiKeys({ workspaceId }));
        }
      } else if (createIntegrationApiKey.rejected.match(result)) {
        const errorMessage = result.payload?.message || t('toast.apiKeyGenerateError');
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error(t('toast.apiKeyGenerateError'));
      console.error('Failed to create API key:', err);
    } finally {
      setSubmittingApiKey(false);
    }
  };

  const onSubmitActiveCampaign = async (data: ActiveCampaignFormValues) => {
    if (!workspaceId) {
      toast.error(t('toast.workspaceNotFound'));
      return;
    }

    setSubmittingActiveCampaign(true);
    try {
      const result = await dispatch(
        setupWorkspaceIntegration({
          workspaceId,
          providerType: 'ACTIVE_CAMPAIGN',
          input: {
            config: {
              api_url: data.apiUrl.trim(),
              api_key: data.apiKey.trim(),
            },
          },
        }),
      );

      if (setupWorkspaceIntegration.fulfilled.match(result)) {
        toast.success(t('toast.activeCampaignConnected'));
        activeCampaignForm.reset();
        dispatch(fetchWorkspaceIntegrations(workspaceId));
        setSheetStep('choose');
        setSheetOpen(false);
      } else if (setupWorkspaceIntegration.rejected.match(result)) {
        toast.error(result.payload?.message ?? t('toast.activeCampaignConnectError'));
      }
    } finally {
      setSubmittingActiveCampaign(false);
    }
  };

  const handleSheetDismiss = () => {
    if (sheetStep === 'choose') {
      setSheetOpen(false);
    } else {
      if (sheetStep === 'activeCampaign') {
        activeCampaignForm.reset();
      }
      setSheetStep('choose');
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    if (!workspaceId) {
      toast.error(t('toast.workspaceNotFound'));
      return;
    }

    if (!window.confirm(t('confirm.deleteApiKey'))) {
      return;
    }

    setError(null);

    try {
      console.log('revokeIntegrationApiKey', workspaceId, apiKeyId);

      const result = await dispatch(
        revokeIntegrationApiKey({
          workspaceId,
          apiKeyId,
        }),
      );

      if (revokeIntegrationApiKey.fulfilled.match(result)) {
        dispatch(fetchIntegrationApiKeys({ workspaceId }));
      } else if (revokeIntegrationApiKey.rejected.match(result)) {
        const errorMessage = result.payload?.message || t('toast.apiKeyDeleteError');
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error(t('toast.apiKeyDeleteError'));
      console.error('Failed to delete API key:', err);
    }
  };

  const handleRotateApiKey = async (apiKeyId: string) => {
    if (!workspaceId) {
      toast.error(t('toast.workspaceNotFound'));
      return;
    }

    if (!window.confirm(t('confirm.rotateApiKey'))) {
      return;
    }

    setError(null);
    setRotatedApiKey(null);

    try {
      const result = await dispatch(
        rotateIntegrationApiKey({
          workspaceId,
          apiKeyId,
        }),
      );

      if (rotateIntegrationApiKey.fulfilled.match(result)) {
        setRotatedApiKey(result.payload);
        if (workspaceId) {
          dispatch(fetchIntegrationApiKeys({ workspaceId }));
        }
      } else if (rotateIntegrationApiKey.rejected.match(result)) {
        const errorMessage = result.payload?.message || t('toast.apiKeyRotateError');
        toast.error(errorMessage);
      }
    } catch (err) {
      toast.error(t('toast.apiKeyRotateError'));
      console.error('Failed to rotate API key:', err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-12">
        <Title level={1}>{t('pageTitle')}</Title>
        <Skeleton className="h-10 w-48" />
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Title level={1}>{t('pageTitle')}</Title>

        <Sheet
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (open) {
              setSheetStep('choose');
              form.reset();
              activeCampaignForm.reset();
              setCreatedApiKey(null);
              setError(null);
            }
          }}
        >
          <SheetTrigger asChild>
            <Button className="text-base w-full sm:w-auto">
              <Plus className="size-6 mr-2 " />
              {t('connectApp')}
            </Button>
          </SheetTrigger>

          <SheetContent
            side="right"
            className="lg:max-w-[784px] w-full px-6 py-8 border-none  sm:rounded-l-[20px] bg-white"
          >
            <SheetHeader className="pb-4">
              <SheetTitle className="flex items-center justify-between">
                <X className="size-6 cursor-pointer" onClick={() => setSheetOpen(false)} />
                <Label className="cursor-pointer" onClick={handleSheetDismiss}>
                  {t('cancel')}{' '}
                </Label>
              </SheetTitle>
            </SheetHeader>

            <Title level={1}>{t('connectApp')}</Title>

            {sheetStep === 'choose' && (
              <div className="space-y-6 mt-6">
                <Title level={2}>{t('sheet.selectApp')}</Title>
                <button
                  type="button"
                  onClick={() => setSheetStep('api')}
                  className="p-4 flex items-stretch border border-input rounded-2xl gap-4 w-full text-left cursor-pointer transition-colors hover:bg-muted/40"
                >
                  <div className="size-16 shrink-0 flex items-center justify-center rounded-lg border border-card bg-subtle">
                    <KeyRound className="size-8 text-secondary" aria-hidden />
                  </div>
                  <div className="space-y-1 flex flex-col flex-1 min-w-0">
                    <Title level={3}>API</Title>
                    <p className="text-sm text-secondary">{t('api.description')}</p>
                  </div>
                  <ChevronRight className="size-6 shrink-0 " aria-hidden />
                </button>

                <button
                  type="button"
                  onClick={() => setSheetStep('activeCampaign')}
                  className="p-4 flex items-stretch border border-input rounded-2xl gap-4 w-full text-left cursor-pointer transition-colors hover:bg-muted/40"
                >
                  <img
                    src="/assets/active-campaign.png"
                    alt="ActiveCampaign"
                    className="size-16 shrink-0 rounded-lg border border-card"
                  />
                  <div className="space-y-1 flex flex-col flex-1 min-w-0">
                    <Title level={3}>ActiveCampaign</Title>
                    <p className="text-sm text-secondary">{t('activeCampaign.description')}</p>
                  </div>
                  <ChevronRight className="size-6 shrink-0 " aria-hidden />
                </button>
              </div>
            )}

            {sheetStep === 'api' && (
              <div className="space-y-6 mt-6">
                <div className="p-4 flex items-stretch rounded-2xl gap-4 w-full text-left bg-subtle">
                  <div className="size-16 shrink-0 flex items-center justify-center rounded-lg border border-card bg-white">
                    <KeyRound className="size-8 text-secondary" aria-hidden />
                  </div>
                  <div className="space-y-1 flex flex-col flex-1 min-w-0">
                    <Title level={3}>API</Title>
                    <p className="text-sm text-secondary">{t('api.descriptionShort')}</p>
                  </div>
                </div>

                <h2 className="text-xl font-medium">{t('api.generateApiKey')}</h2>

                {!createdApiKey && (
                  <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="space-y-2">
                      <Controller
                        name="name"
                        control={form.control}
                        render={({ field }) => (
                          <InputLabel
                            label={t('api.apiKeyName')}
                            id="api-key-name"
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                            maxLength={80}
                          />
                        )}
                      />
                      {form.formState.errors.name && (
                        <p className="text-xs text-error">{form.formState.errors.name.message}</p>
                      )}
                      {error && <p className="text-xs text-error">{error}</p>}
                    </div>

                    <Button
                      type="submit"
                      disabled={submittingApiKey || !workspaceId}
                      className="w-full sm:w-auto"
                    >
                      {submittingApiKey ? t('api.generating') : t('api.generateButton')}
                    </Button>
                  </form>
                )}

                {createdApiKey && (
                  <div className="space-y-4 ">
                    <div className="space-y-2">
                      <p className="text-base font-medium">
                        {t('api.apiKeyCreated', { name: createdApiKey.name })}
                      </p>
                      <label className="text-sm font-medium ">{t('api.apiKeyHint')}</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={createdApiKey.apiKey}
                          className="flex-1 px-4 py-2 border border-input  rounded-md "
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(createdApiKey.apiKey);
                            toast.success(t('toast.apiKeyCopied'));
                          }}
                        >
                          {t('copy')}
                        </Button>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => setSheetOpen(false)}
                    >
                      {t('api.close')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {sheetStep === 'activeCampaign' && (
              <div className="space-y-6 mt-6">
                <div className="p-4 flex items-stretch rounded-2xl gap-4 w-full text-left bg-subtle">
                  <img
                    src="/assets/active-campaign.png"
                    alt="ActiveCampaign"
                    className="size-16 shrink-0 rounded-lg border border-card"
                  />
                  <div className="space-y-1 flex flex-col flex-1 min-w-0">
                    <Title level={3}>ActiveCampaign</Title>
                    <p className="text-sm text-secondary">{t('activeCampaign.description')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <Title level={2}>{t('activeCampaign.connectionSettings')}</Title>
                  <p className="text-sm text-secondary">{t('activeCampaign.connectionHint')}</p>
                  <form
                    className="space-y-4"
                    onSubmit={activeCampaignForm.handleSubmit(onSubmitActiveCampaign)}
                  >
                    <div className="space-y-2">
                      <Controller
                        name="apiUrl"
                        control={activeCampaignForm.control}
                        render={({ field }) => (
                          <InputLabel
                            label={t('activeCampaign.apiUrlLabel')}
                            id="ac-api-url"
                            type="url"
                            value={field.value}
                            onChange={field.onChange}
                            autoComplete="url"
                          />
                        )}
                      />
                      {activeCampaignForm.formState.errors.apiUrl && (
                        <p className="text-xs text-error">
                          {activeCampaignForm.formState.errors.apiUrl.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Controller
                        name="apiKey"
                        control={activeCampaignForm.control}
                        render={({ field }) => (
                          <InputLabel
                            label={t('activeCampaign.apiKeyLabel')}
                            id="ac-api-key"
                            type="password"
                            value={field.value}
                            onChange={field.onChange}
                            autoComplete="off"
                          />
                        )}
                      />
                      {activeCampaignForm.formState.errors.apiKey && (
                        <p className="text-xs text-error">
                          {activeCampaignForm.formState.errors.apiKey.message}
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={
                        submittingActiveCampaign ||
                        !activeCampaignForm.formState.isValid ||
                        !workspaceId
                      }
                      className="w-full sm:w-auto"
                    >
                      {submittingActiveCampaign
                        ? t('activeCampaign.connecting')
                        : t('activeCampaign.connect')}
                    </Button>
                  </form>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>

      <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
        <SheetContent
          side="right"
          className="lg:max-w-[784px] w-full border-none sm:rounded-l-[20px] bg-white flex flex-col"
        >
          <SheetHeader className="pb-6 bg-decorative-2">
            <div className="flex items-start gap-2 justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src="/assets/active-campaign.png"
                  alt="ActiveCampaign"
                  className="size-16 sm:size-24 rounded-full shrink-0"
                />
                <div className="space-y-1 min-w-0 flex-1">
                  <Title level={1}>ActiveCampaign</Title>
                  <p className="text-base text-secondary">{t('activeCampaign.description')}</p>
                </div>
              </div>
              <X
                className="size-6 cursor-pointer self-start shrink-0"
                onClick={() => setDetailsSheetOpen(false)}
              />
            </div>
          </SheetHeader>
          <div className="flex items-center justify-between px-6 py-4">
            <Label>{t('activeCampaign.enabled')}</Label>
            <Switch checked={isActiveCampaignActive} aria-label="ActiveCampaign" />
          </div>
          <div className="mx-6">
            <Separator className="w-80 mx-auto" />
          </div>
          <div className="flex-1" />
          <div className="px-6 pb-6 mt-6">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleDeleteActiveCampaign}
              disabled={deletingActiveCampaign || !workspaceId}
            >
              {t('activeCampaign.deleteApp')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={apiDetailsSheetOpen} onOpenChange={setApiDetailsSheetOpen}>
        <SheetContent
          side="right"
          className="lg:max-w-[784px] w-full border-none sm:rounded-l-[20px] bg-white"
        >
          <SheetHeader className="pb-6 bg-decorative-2">
            <div className="flex items-start gap-2 justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <div className="size-16 sm:size-24 shrink-0 flex items-center justify-center rounded-full bg-white">
                  <KeyRound className="size-8 sm:size-12 text-secondary" aria-hidden />
                </div>
                <div className="space-y-1 min-w-0 flex-1">
                  <Title level={1}>API</Title>
                </div>
              </div>
              <X
                className="size-6 cursor-pointer self-start shrink-0"
                onClick={() => setApiDetailsSheetOpen(false)}
              />
            </div>
          </SheetHeader>
          <div className="space-y-6 px-6">
            {integrationApiKeys && integrationApiKeys.length > 0 ? (
              <div className="space-y-3 ">
                {integrationApiKeys
                  .filter(
                    (apiKey: IntegrationApiKeyDto) =>
                      apiKey.status !== 'REVOKED' && apiKey.status !== 'revoked',
                  )
                  .map((apiKey: IntegrationApiKeyDto) => (
                    <div key={apiKey.keyId} className="space-y-3">
                      <div className="p-4 border rounded-lg border-gray-200  bg-white">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-base font-medium text-gray-900 dark:text-gray-100 flex-1">
                            {apiKey.name}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDeleteApiKey(apiKey.keyId)}
                              disabled={loading}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t('api.deleteApiKeyTitle')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRotateApiKey(apiKey.keyId)}
                              disabled={loading}
                              className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t('api.rotateApiKeyTitle')}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {rotatedApiKey && (
                        <div className="space-y-4 ">
                          <div className="space-y-2">
                            <label className="text-sm font-medium ">{t('api.apiKeyHint')}</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                readOnly
                                value={rotatedApiKey.apiKey}
                                className="flex-1 px-4 py-2 border border-input  rounded-md "
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(rotatedApiKey.apiKey);
                                  toast.success(t('toast.apiKeyCopied'));
                                }}
                              >
                                {t('copy')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('api.noActiveKeys')}</p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Table className="rounded-lg">
        <TableHeader>
          <TableRow>
            <TableHead>{t('table.applications')}</TableHead>
            <TableHead>{t('table.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow
            className="cursor-pointer"
            onClick={
              activeWebhooksApiKeysCount > 0
                ? () => {
                    setApiDetailsSheetOpen(true);
                  }
                : undefined
            }
          >
            <TableCell className="flex items-center gap-2">
              <span className="size-8 shrink-0 flex items-center justify-center rounded-full bg-subtle">
                <KeyRound className="size-4 text-secondary" aria-hidden />
              </span>
              <span>API</span>
            </TableCell>
            <TableCell>
              <Badge variant={webhooksStatus.variant}>{webhooksStatus.label}</Badge>
            </TableCell>
          </TableRow>

          <TableRow
            className="cursor-pointer"
            onClick={
              !isActiveCampaignActive
                ? undefined
                : () => {
                    setDetailsSheetOpen(true);
                  }
            }
          >
            <TableCell className="flex items-center gap-2">
              <img
                src="/assets/active-campaign.png"
                alt="ActiveCampaign"
                className="size-8 rounded-full"
              />
              <span>ActiveCampaign</span>
            </TableCell>
            <TableCell>
              <Badge variant={activeCampaignStatus.variant}>{activeCampaignStatus.label}</Badge>
            </TableCell>
          </TableRow>

          {isStripeConnectEnabled() && (
            <StripeConnectRow workspaceId={workspaceId} canManage={isWorkspaceAdmin} />
          )}
        </TableBody>
      </Table>
    </div>
  );
}
